import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatToDealerTime } from 'utils/dateUtils';
import queryClient from 'app/apiService/queryClient';
import apiService from 'app/apiService';
import RunLogs from 'app/apiService/runLogs/runLogsApiService';
import DataGridTypes from 'types/DataGridTypes';
import RunLogsApiService from '../../apiService/runLogsApiService';
import FailedRecordReason from './FailedRecordReason';

export interface FailedStagingRecord {
  id: string;
  key?: string;
  summary?: string;
  reason?: string;
  failed_at?: string;
  email?: string;
  shopper_id?: string;
  crm_shopper_id?: string;
  crm_opportunity_id?: string;
}

interface FailedRecordsDialogProps {
  open: boolean;
  runId: string;
  actionId: string;
  actionName: string;
  onClose: () => void;
  onRunUpdated: (run: any) => void;
}

interface RetryJob {
  id: string;
  status: string;
  failedReason?: string;
  returnValue?: { succeeded?: number; failed?: number; skipped?: number; clean_failed?: boolean; clean_error?: string };
}

const isJobActive = (job: RetryJob | null) => Boolean(job && !['completed', 'failed'].includes(job.status));

function leadLabel(record: FailedStagingRecord): string {
  return record.summary || record.key || record.id;
}

const FailedRecordsDialog: React.FC<FailedRecordsDialogProps> = ({
  open,
  runId,
  actionId,
  actionName,
  onClose,
  onRunUpdated,
}) => {
  const [records, setRecords] = useState<FailedStagingRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | 'all' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [retryJob, setRetryJob] = useState<RetryJob | null>(null);
  const retryActive = isJobActive(retryJob);
  const onRunUpdatedRef = useRef(onRunUpdated);
  onRunUpdatedRef.current = onRunUpdated;
  const refreshedJobRef = useRef<string | null>(null);

  const loadRecords = useCallback(async () => {
    const response = await RunLogsApiService.getFailedRecords(runId, actionId, {
      skip: 0,
      limit: 50,
    });
    if (!response?.body) throw new Error(response?.message || 'Could not load failed records');
    const body = response.body;
    const rows = Array.isArray(body.data) ? body.data : [];
    setRecords(rows);
    setTotal(typeof body.totalDocuments === 'number' ? body.totalDocuments : rows.length);
    const job: RetryJob | null = body.retry_job || null;
    setRetryJob(job);
    return { rows, job };
  }, [runId, actionId]);

  const refreshRun = useCallback(async () => {
    await apiService.invalidateCache(`${RunLogs.ENDPOINT}/${runId}`, 'GET');
    const response = await RunLogs.getRunByRunId(runId);
    if (response?.body) {
      onRunUpdatedRef.current(response.body);
    }
    queryClient.invalidateQueries({
      queryKey: ['GET', `${DataGridTypes.API_VERSION_2}/run-v2`],
      exact: false,
    });
  }, [runId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setNotice(null);
    setRetryJob(null);
    setLoading(true);
    loadRecords()
      .catch((err: Error) => {
        if (!cancelled) setError(err?.message || 'Could not load failed records');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, loadRecords]);

  useEffect(() => {
    if (!retryJob) return;
    if (retryJob.status === 'failed') {
      setError(retryJob.failedReason || 'Record retry job failed. You can retry again.');
      setNotice(null);
    } else if (retryJob.status === 'completed') {
      setError(null);
      const result = retryJob.returnValue;
      setNotice(result?.clean_failed
        ? `Records were retried, but clean did not start: ${result.clean_error || 'Unknown error'}`
        : `Retry job completed. ${result?.succeeded || 0} succeeded, ${result?.failed || 0} failed, ${result?.skipped || 0} skipped.`);
    } else {
      setError(null);
      setNotice(`Retry job ${retryJob.id} is ${retryJob.status === 'active' ? 'running' : 'queued'}. You can close this dialog; processing continues in the background.`);
    }
  }, [retryJob]);

  useEffect(() => {
    if (!open || !retryJob || isJobActive(retryJob) || refreshedJobRef.current === retryJob.id) return;
    refreshedJobRef.current = retryJob.id;
    void refreshRun().catch((err: any) => setError(err?.message || 'Could not refresh run details'));
  }, [open, retryJob, refreshRun]);

  useEffect(() => {
    if (!open || !retryActive) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const latest = await loadRecords();
        if (cancelled) return;
        if (latest.job && !isJobActive(latest.job)) {
          return;
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Could not refresh retry job status');
      }
      if (!cancelled) timer = setTimeout(poll, 3000);
    };
    timer = setTimeout(poll, 3000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, retryActive, retryJob?.id, loadRecords, refreshRun]);

  const retry = async (ids?: string[], allFailed = false) => {
    setError(null);
    setNotice(null);
    setBusyId(allFailed ? 'all' : ids?.[0] || 'all');
    try {
      const response = await RunLogsApiService.retryFailedRecords(runId, actionId, {
        ids: allFailed ? undefined : ids,
        all_failed: allFailed || undefined,
      });
      const body = response?.body;
      if (!body) {
        throw new Error(response?.message || 'Record retry failed');
      }
      if (body.queued && body.job_id) {
        setRetryJob({ id: body.job_id, status: 'queued' });
      } else if (body.clean_failed) {
        setNotice(
          body.clean_error
            ? `The record was retried, but clean did not start: ${body.clean_error}`
            : 'The record was retried, but clean did not start.',
        );
        await loadRecords();
        await refreshRun();
      } else {
        await loadRecords();
        await refreshRun();
      }
    } catch (err: any) {
      setError(err?.message || 'Record retry failed');
      await loadRecords().catch(() => undefined);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 6 }}>
        Failed records — {actionName}
        <IconButton
          aria-label="close failed records"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Retries are queued as background jobs, then run the same clean for this run again. Rows that
          were already cleaned are left as they are. This is not a full run replay.
        </Typography>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        {notice ? (
          <Alert severity={retryActive ? 'info' : retryJob?.returnValue?.clean_failed ? 'warning' : 'success'} sx={{ mb: 2 }}>
            {notice}
          </Alert>
        ) : null}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : records.length === 0 ? (
          <Typography variant="body2">{retryActive ? 'Records are being retried. Waiting for the job to finish.' : 'No failed records left on this action.'}</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lead</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Shopper / CRM</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Failed at</TableCell>
                <TableCell align="right"> </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map(record => (
                <TableRow key={record.id}>
                  <TableCell>{leadLabel(record)}</TableCell>
                  <TableCell>{record.email || '—'}</TableCell>
                  <TableCell>
                    {[record.shopper_id, record.crm_shopper_id, record.crm_opportunity_id]
                      .filter(Boolean)
                      .join(' / ') || '—'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <FailedRecordReason reason={record.reason} />
                  </TableCell>
                  <TableCell>
                    {record.failed_at ? formatToDealerTime(record.failed_at) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      disabled={Boolean(busyId) || retryActive}
                      onClick={() => retry([record.id])}
                    >
                      {busyId === record.id ? 'Retrying…' : 'Retry'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {total > records.length ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Showing {records.length} of {total}. Retry all covers every failed record on this action.
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
        <Button
          variant="contained"
          disabled={Boolean(busyId) || retryActive || total === 0}
          onClick={() => retry(undefined, true)}
        >
          {busyId === 'all' ? 'Retrying…' : 'Retry all'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FailedRecordsDialog;
